import type { File } from "schemas/generated/file";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@editor/trpc/nextJSProject";

type EditablePageNameProps = {
  page: Pick<File, "id" | "name">;
  projectId: string;
  setIsRenaming: (isRenaming: boolean) => void;
  size?: "sm" | "base";
};

export function EditablePageName({
  page,
  projectId,
  setIsRenaming,
  size = "base",
}: EditablePageNameProps): React.ReactNode {
  const logEvent = useLogAnalytics();
  const [editablePageName, setEditablePageName] = React.useState(page.name);

  const { mutate: updatePageName } = nextJSProjectRouter.updatePage.useMutation(
    {
      onSuccess: () => {
        void nextJSProjectUtils.getPage.invalidate();
        void nextJSProjectUtils.listPages.invalidate();
      },
    },
  );

  const handleSave = () => {
    if (editablePageName.trim() === "") {
      setEditablePageName(page.name);
      setIsRenaming(false);
      return;
    }

    if (editablePageName !== page.name) {
      logEvent("page.rename", {
        origin: "home",
      });
      updatePageName({
        projectId,
        id: page.id,
        updates: {
          newName: editablePageName,
        },
      });
    }
    setIsRenaming(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setEditablePageName(page.name);
      setIsRenaming(false);
    }
  };

  return (
    <Input
      size={size}
      type="text"
      value={editablePageName}
      onChange={(e) => {
        e.stopPropagation();
        setEditablePageName(e.target.value);
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleSave}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      autoFocus
      unsafe_className="w-full"
    />
  );
}
